import React, { lazy, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { DriftUserIF } from '@driftt/app-config/dist/utils/driftTypes'
import {
  Box,
  Card,
  Flex,
  Header,
  InputText,
  Select,
  SelectOption,
  ToggleButton,
  ToggleButtonGroup
} from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import { Metadata } from 'components/Metadata'
import { CurrentUserContext } from 'context/currentUserContext'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { useAppSelector } from 'hooks/useRedux'
import { getAgentDisplayName } from 'utils/activity'
import { segmentTrack, TrackType } from 'utils/tracking'

import AccountsEnrolledTable from './AccountsEnrolledTable'
import { ModalStatus } from './EmailPreviewModal'
import { getStartEndTimeFromRange } from './utils'
const EmailPreviewModal = lazy(() => import('./EmailPreviewModal'))
const EmailDashboard = () => {
  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)
  const currentUser = useContext(CurrentUserContext)
  const orgUsers = Object.values(orgUsersById)
    .filter(user => !!user && user.id !== currentUser?.id)
    .sort((a, b) => {
      const textA = getAgentDisplayName(a).toUpperCase()
      const textB = getAgentDisplayName(b).toUpperCase()
      return textA < textB ? -1 : textA > textB ? 1 : 0
    }) as DriftUserIF[]
  const userOptions: SelectOption[] = useMemo(
    () => [
      { label: getAgentDisplayName(currentUser), value: currentUser?.id ?? '' },
      { label: 'All users', value: 'all' },
      ...orgUsers
        .filter(user => user.status === 'ENABLED')
        .map(user => {
          return { label: getAgentDisplayName(user), value: user.id }
        })
    ],
    [currentUser, orgUsers]
  )
  const defaultUserOption = useMemo(
    () => userOptions.find(option => option.value === currentUser?.id) ?? userOptions[0],
    [currentUser?.id, userOptions]
  )
  const [selectedUser, setSelectedUser] = useState<string>(`${defaultUserOption.value}`)

  const [selectedDateRange, setSelectedDateRange] = useState<string>('today')
  const dateRange = useMemo(() => getStartEndTimeFromRange(selectedDateRange), [selectedDateRange])

  const [modalStatus, setModalStatus] = useState<ModalStatus>({
    open: false,
    accountName: ''
  })

  const openModal = useCallback(
    (eventType: string, accountId: string, accountName: string) => {
      segmentTrack(TrackType.AUTOMATION_EMAIL_PREVIEW, {
        userId: currentUser?.id,
        userRoles: currentUser?.roles,
        orgId: currentUser?.orgId,
        emailType: eventType
      })

      setModalStatus({ open: true, eventType, accountId, accountName, dateRange })
    },
    [currentUser, dateRange]
  )

  const closeModal = useCallback(() => {
    setModalStatus({ open: false, eventType: undefined, accountName: '' })
  }, [])

  const onSelectedUserChange = (selectedUser: SelectOption) => {
    setSelectedUser(`${selectedUser.value}`)
  }
  const onToggleDateChange = (enabledButtons: string[]) => {
    if (enabledButtons.length === 1) {
      setSelectedDateRange(enabledButtons[0])
    }
  }

  const [searchQuery, setSearchQuery] = useState<string>('')

  const handleSearch = useDebouncedCallback((query: string) => {
    setSearchQuery(query)
  }, 300)

  const showUserFilter = access.isAdmin() || access.hasAnyRole('Sales Manager')

  useEffect(() => {
    segmentTrack(TrackType.AUTOMATION_PAGE_VIEW, {
      userId: currentUser?.id,
      userRoles: currentUser?.roles,
      orgId: currentUser?.orgId
    })
  })
  return (
    <Flex
      direction="column"
      css={{ backgroundColor: '$gray1', overflowY: 'scroll' }}
      fullHeight
      fullWidth>
      <Metadata title="Drift | Email dashboard" />
      <Box
        css={{
          padding: '16px 32px',
          backgroundColor: '$white',
          borderBottom: 'solid 1px $gray3',
          width: '100%'
        }}>
        <Header variant="h1">Email dashboard</Header>
      </Box>
      <Flex direction="column" gap={3} fullHeight fullWidth css={{ padding: '32px' }}>
        <Flex justifyContent={showUserFilter ? 'space-between' : 'flex-end'} fullWidth>
          {showUserFilter && (
            <Select
              label=""
              aria-label="filter by user"
              options={userOptions}
              defaultValue={defaultUserOption}
              onChange={newValue => onSelectedUserChange(newValue as SelectOption)}
            />
          )}
          <ToggleButtonGroup
            color="blue"
            appearance="outline"
            onChange={onToggleDateChange}
            exclusive={true}
            initialSelectedValues={['today']}>
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="week">This week</ToggleButton>
            <ToggleButton value="month">This month</ToggleButton>
          </ToggleButtonGroup>
        </Flex>

        <Flex direction="column" gap={0} fullWidth>
          <Card css={{ width: '100%' }}>
            <Card.Header>
              <Header variant="h3">Automated prospecting accounts</Header>
            </Card.Header>
            <Card.Content>
              <InputText
                placeholder="Search"
                hideLabel={true}
                label=""
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
              />
            </Card.Content>
          </Card>
          <AccountsEnrolledTable
            dateRange={dateRange}
            selectedUser={selectedUser}
            openModal={openModal}
            searchQuery={searchQuery}
          />
        </Flex>
      </Flex>
      {modalStatus.open && <EmailPreviewModal modalStatus={modalStatus} closeModal={closeModal} />}
    </Flex>
  )
}

export default EmailDashboard
